import { ArrowLeft } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { usePages } from "../translations/pages";
import Navbar from "../components/Navbar";
import SiteFooter from "../components/Footer";

const PrivacyPolicy = () => {
  const { language } = useLanguage();
  const tp = { ...usePages(language).legal, back: usePages(language).back };
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-page">
      <Navbar isScrolled={true} />
      <div className="wrap-cayus pt-28 pb-16">
        {/* Back link */}
        <a
          href="/"
          className="inline-flex items-center gap-2 text-sm text-[#5E6C83] hover:text-[#1B1B1B] mb-10 transition-colors"
          data-testid="privacy-back-link"
        >
          <ArrowLeft className="w-4 h-4" />
          {tp.back}
        </a>

        <div className="rule-cobalt mb-5" />

        <p className="mono-cayus text-[#0047AB] mb-4">{tp.privacyEyebrow}</p>

        <h1 className="text-3xl sm:text-4xl font-bold text-[#1B1B1B] tracking-tight mb-2">
          Privacy Policy
        </h1>
        <p className="text-sm text-[#5E6C83] mb-10">Cayus Limited. Last updated: March 2026</p>

        <p className="text-sm text-[#5E6C83] bg-[#F7F9FC] border border-[#C3CFE0] rounded-lg px-4 py-3 mb-8 max-w-[70ch]">{tp.refNote}</p>
        <div className="prose-cayus max-w-[70ch]">

          {/* 1. Identity */}
          <h2>1. Identity Information</h2>
          <p>
            <strong>Cayus Limited</strong><br />
            Private limited company registered in England &amp; Wales<br />
            Company number: 17046973<br />
            Registered office: 15 Lea Road, Sevenoaks, TN13 1QP, United Kingdom<br />
            Email: <a href="mailto:privacy@cayus.ai" className="text-[#1d4ed8] hover:underline">privacy@cayus.ai</a>
          </p>
          <p><strong>Website hosting provider:</strong><br />
          The cayus.ai website is hosted on infrastructure provided by Emergent.sh.</p>
          <p><strong>Technical hosting details (confirmed by Emergent.sh):</strong></p>
          <ul>
            <li><strong>Application servers:</strong> Google Cloud Platform (GCP), region us-central1 (Iowa, USA)</li>
            <li><strong>Database:</strong> MongoDB Atlas hosted on GCP, region us-central1 (Iowa, USA)</li>
            <li><strong>CDN &amp; security:</strong> Cloudflare global network</li>
            <li><strong>Payments (if applicable):</strong> Stripe</li>
            <li><strong>Analytics:</strong> PostHog and Google Analytics 4</li>
            <li><strong>Secrets management:</strong> HashiCorp Vault</li>
            <li><strong>Backup and failover region:</strong> pending confirmation from Emergent.sh</li>
          </ul>

          {/* 2. Purpose */}
          <h2>2. Purpose of the Website</h2>
          <p>
            Cayus Limited operates the website cayus.ai to present its software, provide information about its services, and enable businesses to contact Cayus for previews, demos, and commercial discussions.
          </p>
          <p>
            This Privacy Policy explains how personal data is collected and processed when using the cayus.ai website.<br />
            <em>(These terms do not govern the Cayus SaaS platform, which is covered by a separate notice.)</em>
          </p>

          {/* 3. Data Controller */}
          <h2>3. Data Controller</h2>
          <p>Cayus Limited acts as the Data Controller for all personal data collected through the cayus.ai website.</p>

          {/* 4. Data Collected */}
          <h2>4. Data Collected</h2>
          <p>We collect only the data strictly necessary for the functioning of the website and the processing of enquiries.</p>

          <h3>A. Data collected via contact form</h3>
          <ul>
            <li>First name</li>
            <li>Last name</li>
            <li>Work email</li>
            <li>Company</li>
            <li>Job title</li>
            <li>Company size</li>
            <li>Optional message</li>
          </ul>

          <h3>B. Technical &amp; usage data</h3>
          <p>Collected automatically when visiting the website:</p>
          <ul>
            <li>IP address</li>
            <li>Device and browser information</li>
            <li>Pages visited and actions taken</li>
            <li>Session statistics</li>
            <li>Referring website</li>
          </ul>
          <p>Collected by:</p>
          <ul>
            <li>Google Analytics 4 (after consent)</li>
            <li>PostHog (after consent)</li>
            <li>Essential cookies (always on)</li>
          </ul>

          <h3>C. Cookies</h3>
          <p>We use:</p>
          <ul>
            <li><strong>Essential cookies:</strong> required for the website to operate</li>
            <li><strong>Analytics cookies:</strong> only activated after explicit user consent</li>
          </ul>
          <p>We do not use advertising pixels or cross-site marketing trackers.</p>

          {/* 5. Legal Basis */}
          <h2>5. Legal Basis for Processing</h2>
          <p>We process personal data under the following legal grounds:</p>
          <ul>
            <li><strong>Legitimate interests:</strong> responding to enquiries, ensuring website security, understanding how the site is used</li>
            <li><strong>Consent:</strong> activation of analytics cookies</li>
            <li><strong>Pre-contractual measures:</strong> handling demo or preview requests</li>
            <li><strong>Legal obligation:</strong> security, fraud prevention, compliance requirements</li>
          </ul>

          {/* 6. Data Usage */}
          <h2>6. Data Usage</h2>
          <p>Personal data collected on cayus.ai is used exclusively for:</p>
          <ul>
            <li>Responding to enquiries sent via the contact form</li>
            <li>Scheduling demos and commercial discussions</li>
            <li>Managing and qualifying leads</li>
            <li>Operating and improving the website</li>
            <li>Producing analytics (only after consent)</li>
            <li>Ensuring site security and performance</li>
          </ul>
          <p><strong>We do not sell or rent personal data.</strong></p>

          {/* 7. Data Sharing */}
          <h2>7. Data Sharing and Third Parties</h2>
          <p>We share limited data with selected providers who support our website operations. These providers act as Data Processors.</p>

          <div className="overflow-x-auto">
            <table className="min-w-full border border-[#D9D9D9] rounded-lg">
              <thead>
                <tr className="bg-[#F7F9FC]">
                  <th className="text-left p-3 border-b border-[#D9D9D9] font-semibold">Provider</th>
                  <th className="text-left p-3 border-b border-[#D9D9D9] font-semibold">Purpose</th>
                  <th className="text-left p-3 border-b border-[#D9D9D9] font-semibold">Location</th>
                </tr>
              </thead>
              <tbody>
                <tr><td className="p-3 border-b border-[#ECECEC]">Google Cloud Platform</td><td className="p-3 border-b border-[#ECECEC]">Hosting, compute, storage, logs</td><td className="p-3 border-b border-[#ECECEC]">USA (us-central1)</td></tr>
                <tr><td className="p-3 border-b border-[#ECECEC]">MongoDB Atlas</td><td className="p-3 border-b border-[#ECECEC]">Database hosting</td><td className="p-3 border-b border-[#ECECEC]">USA (us-central1)</td></tr>
                <tr><td className="p-3 border-b border-[#ECECEC]">Cloudflare</td><td className="p-3 border-b border-[#ECECEC]">CDN, DNS, DDoS protection</td><td className="p-3 border-b border-[#ECECEC]">Global network</td></tr>
                <tr><td className="p-3 border-b border-[#ECECEC]">PostHog</td><td className="p-3 border-b border-[#ECECEC]">Analytics &amp; event tracking</td><td className="p-3 border-b border-[#ECECEC]">Global</td></tr>
                <tr><td className="p-3 border-b border-[#ECECEC]">Google Analytics 4</td><td className="p-3 border-b border-[#ECECEC]">Website analytics</td><td className="p-3 border-b border-[#ECECEC]">Global</td></tr>
                <tr><td className="p-3 border-b border-[#ECECEC]">Stripe</td><td className="p-3 border-b border-[#ECECEC]">Payments (if used)</td><td className="p-3 border-b border-[#ECECEC]">Global</td></tr>
                <tr><td className="p-3 border-b border-[#ECECEC]">Microsoft 365 (Outlook)</td><td className="p-3 border-b border-[#ECECEC]">Email reception of form submissions</td><td className="p-3 border-b border-[#ECECEC]">UK/EU/Global</td></tr>
                <tr><td className="p-3 border-b border-[#ECECEC]">Emergent.sh</td><td className="p-3 border-b border-[#ECECEC]">Application deployment and runtime</td><td className="p-3 border-b border-[#ECECEC]">USA (via GCP)</td></tr>
              </tbody>
            </table>
          </div>
          <p>All providers implement security and GDPR-aligned safeguards.</p>

          {/* 8. International Transfers */}
          <h2>8. International Data Transfers</h2>
          <p>Because the website is hosted on GCP us-central1 and uses Cloudflare's global edge network, personal data may be transferred outside the UK.</p>
          <p>To ensure adequate protection, we rely on:</p>
          <ul>
            <li>Standard Contractual Clauses (SCCs)</li>
            <li>UK International Data Transfer Addendum (UK Addendum)</li>
            <li>Appropriate technical and organisational safeguards offered by sub-processors</li>
          </ul>
          <p>These measures ensure that your data receives UK GDPR-equivalent protection.</p>

          {/* 9. Data Retention */}
          <h2>9. Data Retention</h2>
          <h3>A. Contact form submissions</h3>
          <p>Retained until the lead becomes a customer or is explicitly deleted.</p>
          <h3>B. Analytics data</h3>
          <p>Google Analytics 4 retention: 14 months.<br />PostHog retention: according to PostHog's standard retention configuration.</p>
          <h3>C. Email inbox</h3>
          <p>Emails received via privacy@cayus.ai and internal contact channels are retained with no defined retention period at this stage. A formal retention policy will be introduced later.</p>

          {/* 10. Security */}
          <h2>10. Security Measures</h2>
          <p>We implement appropriate technical and organisational measures to protect personal data, including:</p>
          <ul>
            <li>HTTPS/TLS encryption</li>
            <li>Encryption at rest (via GCP, MongoDB Atlas)</li>
            <li>DDoS and edge security (Cloudflare)</li>
            <li>Access controls and role-based permissions</li>
            <li>Secrets management (HashiCorp Vault)</li>
            <li>Log monitoring and infrastructure monitoring</li>
          </ul>

          {/* 11. Rights */}
          <h2>11. Rights of Data Subjects</h2>
          <p>Under UK GDPR, you have the right to:</p>
          <ul>
            <li>Access your personal data</li>
            <li>Rectify inaccurate data</li>
            <li>Request deletion of your data</li>
            <li>Restrict processing</li>
            <li>Object to processing</li>
            <li>Request portability of your data</li>
            <li>Withdraw consent at any time (for analytics cookies)</li>
          </ul>
          <p>To exercise your rights, contact: <a href="mailto:privacy@cayus.ai" className="text-[#1d4ed8] hover:underline">privacy@cayus.ai</a></p>
          <p>You also have the right to lodge a complaint with the UK Information Commissioner's Office (ICO).</p>

          {/* 12. Cookies */}
          <h2>12. Cookies</h2>
          <p>Our cookie banner allows you to:</p>
          <ul>
            <li>Accept all cookies</li>
            <li>Reject all cookies</li>
          </ul>
          <p>Customisation of cookie categories is not yet available. Analytics cookies are only activated after acceptance.</p>

          {/* 13. Liability */}
          <h2>13. Liability and Use of the Website</h2>
          <p>Cayus Limited makes every effort to ensure reliable website access. However, we cannot be held liable for:</p>
          <ul>
            <li>Website interruptions</li>
            <li>Technical malfunction</li>
            <li>Data loss caused by third parties</li>
            <li>Issues related to the global internet network</li>
          </ul>
          <p>Any external websites linked from cayus.ai remain the responsibility of their respective owners.</p>

          {/* 14. Changes */}
          <h2>14. Changes to the Privacy Policy</h2>
          <p>Cayus Limited reserves the right to update this Privacy Policy at any time. Updates will be posted on this page with an updated "Last Updated" date.</p>

          {/* 15. Contact */}
          <h2>15. Contact</h2>
          <p>For any question relating to this Privacy Policy:</p>
          <p>
            <strong>Cayus Limited</strong><br />
            15 Lea Road, Sevenoaks<br />
            TN13 1QP<br />
            United Kingdom<br />
            Email: <a href="mailto:privacy@cayus.ai" className="text-[#1d4ed8] hover:underline">privacy@cayus.ai</a>
          </p>
        </div>
      </div>
    <SiteFooter />
    </div>
  );
};

export default PrivacyPolicy;
